import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { REQUIRED_TABLE_COLUMN, type TableColumn, type TableSettings } from '@jisy/shared';
import styles from './ColumnsMenu.module.css';

/**
 * Состав и порядок колонок таблицы.
 *
 * Мышью колонки переставляются перетаскиванием заголовков; здесь то же самое сделано кнопками —
 * иначе порядок нельзя было бы изменить с клавиатуры. Раздел показывается в списке всегда,
 * хотя в таблицу попадает только в режиме «Все разделы»: иначе его галочка исчезала бы
 * вместе с возможностью её вернуть.
 */
export function ColumnsMenu({
  settings,
  onToggle,
  onMove,
  onReset,
}: {
  settings: TableSettings;
  onToggle: (column: TableColumn) => void;
  onMove: (column: TableColumn, direction: -1 | 1) => void;
  onReset: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const hiddenCount = settings.hidden.length;

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={styles.trigger}
          // Число скрытых колонок живёт в подсказке, а не в цвете кнопки: состав колонок —
          // личная настройка вида, а не фильтр, и подсвечивать её как активный отбор незачем.
          {...(hiddenCount > 0
            ? { title: t('table.columnsMenu.selected', { count: hiddenCount }) }
            : {})}
        >
          <span>{t('table.columnsMenu.name')}</span>
          <span className={styles.chevron} aria-hidden="true">
            ▾
          </span>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className={styles.content} align="start" sideOffset={6}>
          <div className={styles.list} role="group" aria-label={t('table.columnsMenu.name')}>
            {settings.order.map((column, index) => {
              const label = t(`table.columns.${column}`);
              const required = column === REQUIRED_TABLE_COLUMN;

              return (
                <div className={styles.option} key={column}>
                  <label className={styles.check}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={!settings.hidden.includes(column)}
                      disabled={required}
                      onChange={() => onToggle(column)}
                    />
                    <span className={`${styles.optionLabel} truncate`}>{label}</span>
                  </label>

                  <button
                    type="button"
                    className={styles.move}
                    onClick={() => onMove(column, -1)}
                    disabled={index === 0}
                    aria-label={t('table.columnsMenu.moveUp', { name: label })}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={styles.move}
                    onClick={() => onMove(column, 1)}
                    disabled={index === settings.order.length - 1}
                    aria-label={t('table.columnsMenu.moveDown', { name: label })}
                  >
                    ↓
                  </button>
                </div>
              );
            })}
          </div>

          <p className={styles.hint}>{t('table.columnsMenu.hint')}</p>

          <button type="button" className={styles.reset} onClick={onReset}>
            {t('table.columnsMenu.reset')}
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
